import { range } from 'lodash-es';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useCurrentThemeIsDark } from 'utils/theme';
import type { Weekdays } from 'utils/work-days';
import { weekdayToISONumberMap } from 'utils/work-days';
import { ZOOM_LEVELS } from './gantt-zoom';

// Returns a CSS background style for non-workdays using linear gradients
const createByZoomAndWorkdays = (
  zoomLevel: number,
  workdays: Array<string>,
  themeIsDark: boolean
) => {
  const zoom = ZOOM_LEVELS[zoomLevel];
  const workdaysIndices = workdays.map(day => weekdayToISONumberMap[day as Weekdays] - 1);
  const { columnWidth } = zoom;
  const color = themeIsDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
  const dividerColor = themeIsDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';

  // Non-workday bands
  const nonWorkdayBands = range(0, 7)
    .filter(day => !workdaysIndices.includes(day))
    .map(
      day =>
        `linear-gradient(to right, transparent ${day * columnWidth}px, ${color} ${
          day * columnWidth
        }px, ${color} ${(day + 1) * columnWidth}px, transparent ${(day + 1) * columnWidth}px)`
    )
    .join(',');

  // Day dividers as a repeating-linear-gradient
  const dayDividers = `repeating-linear-gradient(to right, ${dividerColor}, ${dividerColor} 1px, transparent 1px, transparent ${columnWidth}px)`;

  return {
    backgroundImage: [nonWorkdayBands, dayDividers].filter(Boolean).join(','),
    backgroundSize: `${columnWidth * 7}px 10px`,
    backgroundRepeat: 'repeat',
  };
};

/**
 * Instead of constructing a background from lots and lots of divs and tracking it around,
 * we just use a repeating CSS linear-gradient as a background image.
 * We can size to the zoom level and offset it to the appropriate position.
 */
export const useWeekdayBackground = (
  zoomLevel: number,
  rowStartDate: string,
  workdays: Array<string>
) => {
  const zoom = ZOOM_LEVELS[zoomLevel];
  const { columnWidth, dayPadding } = zoom;
  const themeIsDark = useCurrentThemeIsDark();
  const baseStyle = useMemo(
    () => createByZoomAndWorkdays(zoomLevel, workdays, themeIsDark),
    [workdays, zoomLevel, themeIsDark]
  );
  const weekOffset = useMemo(() => DateTime.fromISO(rowStartDate).weekday - 1, [rowStartDate]);

  return useMemo(
    () => ({
      ...baseStyle,
      backgroundPosition: `${weekOffset * -columnWidth - dayPadding / 2}px 0`,
    }),
    [baseStyle, weekOffset, columnWidth, dayPadding]
  );
};
